import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  Image,
  Link as ChakraLink,
  Stack,
  Text,
  Icon,
  Container,
} from "@chakra-ui/react";
import { IoPersonCircleOutline, IoLogOutOutline } from "react-icons/io5";
import { getDecodedToken } from "../services/Token";

// Configuraciones constantes para facilitar mantenimiento
const NAV_ITEMS = [
  { name: "Carreras", path: "/carreras" },
  { name: "Instituciones", path: "/instituciones" },
  { name: "Juego", path: "/info" },
  { name: "Toma de decision", path: "/decision" },
  { name: "Eventos", path: "/eventos" },
  { name: "Noticias", path: "/noticias" },
  { name: "Administrar carreras", path: "/dashboard/carreras", isAdmin: true },
];

export default function NavbarHome() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const decoded = getDecodedToken();

  const visibleLinks = useMemo(() => {
    return NAV_ITEMS.filter(item => 
      !item.isAdmin || (decoded && decoded.rol_id === 2)
    );
  }, [decoded]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <Box 
      as="nav" 
      bg="#E9681F"
      px={4} 
      position="sticky" 
      top="0" 
      zIndex="sticky"
      boxShadow="md"
    >
      <Container maxW="container.xl">
        <Flex h={16} alignItems="center" justifyContent="space-between">
          
          {/* Botón Menú Móvil */}
          <IconButton
            size="md"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Toggle Navigation"
            display={{ md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            bg="transparent"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
          />

<HStack spacing={4} alignItems={"center"} h="full"> 
  <Box 
    onClick={() => navigate("/")} 
    cursor="pointer"
    h="full" // Ocupa todo el alto del navbar
    display="flex"
    alignItems="center"
  >
    <Image
      src="/simbolo.png"
      alt="Logo Goya"
      // Usamos un valor ligeramente superior al h del navbar para que resalte
      // o ajustamos al 90% para que no toque los bordes
      h="200%" 
      minW="120px" // Asegura que no se contraiga a lo ancho
      objectFit="contain"
      transition="transform 0.2s"
      _hover={{ transform: "scale(1.1)" }}
    />
  </Box>
            <HStack as="nav" spacing={4} display={{ base: "none", md: "flex" }}>
              {visibleLinks.map((link) => (
                <NavLink 
                  key={link.name} 
                  name={link.name} 
                  path={link.path} 
                  isActive={location.pathname === link.path} 
                />
              ))}
            </HStack>
          </HStack>

 
          <Flex alignItems="center">
            {decoded ? (
              <Menu isLazy>
                <MenuButton
                  as={Button}
                  rounded="full"
                  variant="link"
                  cursor="pointer"
                  minW={0}
                >
                  <Icon as={IoPersonCircleOutline} boxSize={10} color="white" />
                </MenuButton>
                <MenuList color="black">
                  <Box px={3} py={2}>
                    <Text fontSize="xs" color="gray.500">Sesión iniciada como</Text>
                    <Text fontSize="sm" fontWeight="bold">{decoded.sub || 'Usuario'}</Text>
                  </Box>
                  <MenuDivider />
                  <MenuItem 
                    onClick={handleLogout} 
                    icon={<IoLogOutOutline />} 
                    color="red.500"
                  >
                    Cerrar Sesión
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <NavLink name="Ingresar" path="/login" isActive={location.pathname === "/login"} />
            )}
          </Flex>
        </Flex>

        {isOpen && (
          <Box pb={4} display={{ md: "none" }}>
            <Stack as="nav" spacing={2}>
              {visibleLinks.map((link) => (
                <NavLink 
                  key={link.name} 
                  name={link.name} 
                  path={link.path} 
                  isActive={location.pathname === link.path}
                  onClose={onClose}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}

const NavLink = ({ 
  name, 
  path, 
  isActive, 
  onClose 
}: { 
  name: string; 
  path: string; 
  isActive: boolean; 
  onClose?: () => void 
}) => {
  const navigate = useNavigate();

  return (
    <ChakraLink
      as="button"
      onClick={() => {
        navigate(path);
        if (onClose) onClose();
      }}
      px={3}
      py={1}
      rounded="md"
      color="white"
      fontWeight={isActive ? "bold" : "medium"}
      fontSize="lg"
      bg={isActive ? "whiteAlpha.300" : "transparent"}
      transition="0.2s"
      _hover={{
        textDecoration: "none",
        bg: "whiteAlpha.200",
      }}
      textAlign="left"
      w={{ base: "full", md: "auto" }}
    >
      {name}
    </ChakraLink>
  );
};

